// Process text to handle Slack-style links
export function processSlackLinks(text: string): string {
  let result = text;

  // Handle reference-style links: [1]: <url|text>
  result = result.replace(
    /(\[\d+\]:\s*)<(https?:\/\/[^|>]+)\|([^>]+)>/g,
    (match, prefix, url, linkText) => {
      const escapedPrefix = prefix.replace(/\[(\d+)\]/, "\\[$1\\]");
      return `${escapedPrefix}[${linkText}](${url})`;
    }
  );

  // Handle inline Slack links with display text: <url|display text>
  result = result.replace(
    /<(https?:\/\/[^|>]+)\|([^>]+)>/g,
    (_match, url, linkText) => `[${linkText}](${url})`
  );

  // Handle inline Slack links without display text: <url>
  result = result.replace(
    /<(https?:\/\/[^>]+)>/g,
    (_match, url) => `[${url}](${url})`
  );

  return result;
}

// Process Slack subteam mentions to render as backtick-wrapped handles
export function processSlackSubteamMentions(text: string): string {
  // Replace <!subteam^ID|@handle> with `@handle`
  // Replace <!subteam^ID> (without handle) with `@team`
  return text.replace(
    /<!subteam\^([^|>]+)(?:\|(@?[^>]+))?>/g,
    (_match, _id, handle) => {
      if (handle) {
        const displayHandle = handle.startsWith("@") ? `${handle}` : `@${handle}`;
        return `\`${displayHandle}\``;
      }
      return "`@team`";
    }
  );
}

// Process Slack special mentions like <!here>, <!channel>, <!everyone>
export function processSlackSpecialMentions(text: string): string {
  return text.replace(
    /<!(\w+)(?:\|([^>]+))?>/g,
    (_match, keyword, label) => {
      const display = label || keyword;
      return `\`@${display}\``;
    }
  );
}

export function processSlackUserMentions(text: string): string {
  // Replace <@USER_ID|name> with `@name`
  // Replace <@USER_ID> with `@🔒[UNKNOWN USER]`

  return text.replace(
    /<@(U[A-Z0-9]+)(?:\|([^>]+))?>/g,
    (match, userId, userName) => {
      if (userName && userName.trim()) {
        return `\`@${userName}\``;
      } else {
        return "`@🔒[UNKNOWN USER]`";
      }
    }
  );
}

// Process Slack channel mentions to render as backtick-wrapped handles
export function processSlackChannelMentions(text: string): string {
  // Replace <#CHANNEL_ID|name> with a link of the form [#name](https://slack.com/app_redirect?channel=CHANNEL_ID)
  // Replace <#CHANNEL_ID> (no name) with `#unknown-or-private-channel`

  return text.replace(
    /<#([CG][A-Z0-9]+)(?:\|([^>]+))?>/g,
    (match, channelId, channelName) => {
      if (channelName && channelName.trim()) {
        // Channel has a name, create a clickable link
        return `[#${channelName}](https://slack.com/app_redirect?channel=${channelId})`;
      } else {
        // No channel name, use generic text in backticks
        return `[#🔒[UNKNOWN OR PRIVATE]](https://slack.com/app_redirect?channel=${channelId})`;
      }
    }
  );
}

// Process text to wrap step references with backticks
export function processStepReferences(text: string): string {
  return text; // Disabled for now, Backend should be updated to add backticks
  // Replace ††step_X†† with `††step_X††` to make it render as code
  // return text.replace(
  //   /††step_[A-Za-z0-9_]+††/g,
  //   (match) => `\`${match}\``
  // );
}

// Fix code block formatting - ensure all ``` are followed by newlines
export function processCodeBlocks(text: string): string {
  // Handle code blocks with language identifiers like ```text or ```javascript
  // Replace ```text\ncontent with ```\ncontent (remove the language identifier)
  return text.replace(/```([a-zA-Z0-9]+)\n/g, "```\n");
}

// Extract referenced source IDs from markdown content
export function extractReferencedSourceIds(text: string): Set<string> {
  const referencedSourceIds = new Set<string>();

  // Match all source references in the content
  // Format: ††source_nodeId_sourceId††
  const sourceReferenceRegex = /††source_([A-Za-z0-9_]+)_([A-Za-z0-9_]+)††/g;
  let match;

  while ((match = sourceReferenceRegex.exec(text)) !== null) {
    const nodeId = match[1];
    const sourceId = match[2];
    const fullSourceId = `${nodeId}_${sourceId}`;
    referencedSourceIds.add(fullSourceId);
  }

  return referencedSourceIds;
}

// Extract tool IDs from a message (for MessageProcessor compatibility)
export function extractToolIds(text: string): string[] {
  const toolIds: string[] = [];
  const tooluRegex = /††(toolu_[a-zA-Z0-9_]+)††/g;
  const toolRegex = /††(tool_[a-zA-Z0-9_]+)††/g;
  const callRegex = /††(call_[a-zA-Z0-9_]+)††/g;
  const artifactRegex = /††(artifact_[\w-]+\.json)††/g;
  let match;

  while ((match = callRegex.exec(text)) !== null) {
    toolIds.push(match[1]);
  }
  while ((match = tooluRegex.exec(text)) !== null) {
    toolIds.push(match[1]);
  }
  while ((match = toolRegex.exec(text)) !== null) {
    toolIds.push(match[1]);
  }
  while ((match = artifactRegex.exec(text)) !== null) {
    toolIds.push(match[1]);
  }

  return toolIds;
}

// Check if text contains tool references
export function containsToolReferences(text: string): boolean {
  return typeof text === "string" && (
    text.includes("††toolu_") ||
    text.includes("††tool_") ||
    text.includes("††call_") ||
    text.includes("††") && text.includes(".json††")  // V2 artifact citations
  );
}

// Process tool references and convert them to a format Markdown can handle
export function processToolReferences(text: string, sources: readonly any[]): string {
  if (!containsToolReferences(text) || !sources || sources.length === 0) {
    return text;
  }

  // Remove tool references that don't have sources
  let processedText = text;

  // Handle tool references that are already wrapped in backticks (grouped)
  const callRegex = /`††(call_[a-zA-Z0-9_]+)††`(?:, `††call_[a-zA-Z0-9_]+††`)*/g;
  processedText = processedText.replace(callRegex, (match) => {
    // Extract all tool IDs from this match
    const callMatches = [...match.matchAll(/††(call_[a-zA-Z0-9_]+)††/g)];
    const callIds = callMatches.map(m => m[1]);

    // Filter for tool IDs that have sources
    const callIdsWithSources = callIds.filter(callId =>
      sources.some((source) => source.toolId === callId)
    );

    // If no tools have sources, remove the reference
    if (callIdsWithSources.length === 0) {
      return "";
    }

    // Return the tool references with backticks
    if (callIdsWithSources.length === 1) {
      return "`††" + callIdsWithSources[0] + "††`";
    } else {
      // Multiple tools - create a list
      return callIdsWithSources.map(callId => "`††" + callId + "††`").join(", ");
    }
  });

  // Handle tool references that are already wrapped in backticks (grouped)
  const tooluRegex = /`††(toolu_[a-zA-Z0-9_]+)††`(?:, `††toolu_[a-zA-Z0-9_]+††`)*/g;
  processedText = processedText.replace(tooluRegex, (match) => {
    // Extract all tool IDs from this match
    const toolMatches = [...match.matchAll(/††(toolu_[a-zA-Z0-9_]+)††/g)];
    const toolIds = toolMatches.map(m => m[1]);

    // Filter for tool IDs that have sources
    const toolIdsWithSources = toolIds.filter(toolId =>
      sources.some((source) => source.toolId === toolId)
    );

    // If no tools have sources, remove the reference
    if (toolIdsWithSources.length === 0) {
      return "";
    }

    // Return the tool references with backticks
    if (toolIdsWithSources.length === 1) {
      return "`††" + toolIdsWithSources[0] + "††`";
    } else {
      // Multiple tools - create a list
      return toolIdsWithSources.map(toolId => "`††" + toolId + "††`").join(", ");
    }
  });
  const toolRegex = /`††(tool_[a-zA-Z0-9_]+)††`(?:, `††tool_[a-zA-Z0-9_]+††`)*/g;
  processedText = processedText.replace(toolRegex, (match) => {
    // Extract all tool IDs from this match
    const toolMatches = [...match.matchAll(/††(tool_[a-zA-Z0-9_]+)††/g)];
    const toolIds = toolMatches.map(m => m[1]);

    // Filter for tool IDs that have sources
    const toolIdsWithSources = toolIds.filter(toolId =>
      sources.some((source) => source.toolId === toolId)
    );

    // If no tools have sources, remove the reference
    if (toolIdsWithSources.length === 0) {
      return "";
    }

    // Return the tool references with backticks
    if (toolIdsWithSources.length === 1) {
      return "`††" + toolIdsWithSources[0] + "††`";
    } else {
      // Multiple tools - create a list
      return toolIdsWithSources.map(toolId => "`††" + toolId + "††`").join(", ");
    }
  });

  // Handle individual tool references that are not wrapped in backticks or not in groups
  const individualCallRegex = /`?††(call_[a-zA-Z0-9_]+)††`?/g;
  processedText = processedText.replace(individualCallRegex, (match, toolId) => {
    // Check if this tool has a source
    const hasSource = sources.some((source) => source.toolId === toolId);

    if (hasSource) {
      // Wrap in backticks if not already wrapped
      return match.includes("`") ? match : `\`††${toolId}††\``;
    } else {
      // Remove the tool reference if it doesn't have a source
      return "";
    }
  });
  // Handle individual tool references that are not wrapped in backticks or not in groups
  const individualTooluRegex = /`?††(toolu_[a-zA-Z0-9_]+)††`?/g;
  processedText = processedText.replace(individualTooluRegex, (match, toolId) => {
    // Check if this tool has a source
    const hasSource = sources.some((source) => source.toolId === toolId);

    if (hasSource) {
      // Wrap in backticks if not already wrapped
      return match.includes("`") ? match : `\`††${toolId}††\``;
    } else {
      // Remove the tool reference if it doesn't have a source
      return "";
    }
  });
  // Handle individual tool references that are not wrapped in backticks or not in groups
  const individualToolRegex = /`?††(tool_[a-zA-Z0-9_]+)††`?/g;
  processedText = processedText.replace(individualToolRegex, (match, toolId) => {
    // Check if this tool has a source
    const hasSource = sources.some((source) => source.toolId === toolId);

    if (hasSource) {
      // Wrap in backticks if not already wrapped
      return match.includes("`") ? match : `\`††${toolId}††\``;
    } else {
      // Remove the tool reference if it doesn't have a source
      return "";
    }
  });

  // Handle artifact references (grouped) - V2 format without artifact_ prefix
  const artifactGroupRegex = /`††([\w-]+\.json)††`(?:, `††[\w-]+\.json††`)*/g;
  processedText = processedText.replace(artifactGroupRegex, (match) => {
    const artifactMatches = [...match.matchAll(/††([\w-]+\.json)††/g)];
    const artifactIds = artifactMatches.map(m => m[1]);

    const artifactIdsWithSources = artifactIds.filter(artifactId =>
      sources.some((source) => source.toolId === artifactId)
    );

    if (artifactIdsWithSources.length === 0) return "";

    if (artifactIdsWithSources.length === 1) {
      return "`††" + artifactIdsWithSources[0] + "††`";
    } else {
      return artifactIdsWithSources.map(id => "`††" + id + "††`").join(", ");
    }
  });

  // Handle individual artifact references
  const individualArtifactRegex = /`?††(artifact_[\w-]+\.json)††`?/g;
  processedText = processedText.replace(individualArtifactRegex, (match, artifactId) => {
    const hasSource = sources.some((source) => source.toolId === artifactId);

    if (hasSource) {
      return match.includes("`") ? match : `\`††${artifactId}††\``;
    } else {
      return "";
    }
  });

  return processedText;
}
